/** Record of actions history. */
export type Record<T> = {
  /** What will be done. */
  Redo: (t: T)=>T;
  /** What will be undone then. */
  Undo: (t: T)=>T;
}

/** Class that implements all functionality of history. */
const createHist = <T,>(depth_: number) => {
  let list_: Record<T>[] = [];
  let offset_ = 0;

  /** Add new record to a history, then `r.Redo()` will be called automatically. */
  const add = (r: Record<T>, t: T): T => {
    let lst = list_.slice(0, list_.length-offset_);
    if (lst.length === depth_) {
      lst = lst.splice(0, 1);
    }
    if (lst.length === depth_) return t;
    lst.push(r);
    offset_ = 0;
    list_ = lst;
    return r.Redo(t);
  }

  /** Undo the last record in a history. */
  const undo = (t: T): T => {
    let result = t;
    if (list_.length) {
      let maxOffset = list_.length-1;
      if (offset_ <= maxOffset) {
        result = list_[maxOffset-offset_].Undo(t);
        offset_++;
      }
    }
    return result;
  }

  /** Redo last undone record and return it back to the history. */
  const redo = (t: T): T => {
    let result = t;
    if (list_.length) {
      let maxOffset = list_.length-1;
      if (offset_ > 0) {
        offset_--;
        result = list_[maxOffset-offset_].Redo(t);
      }
    }
    return result;
  }

  /** Clean the history. */
  const clean = () => {
    list_ = [];
    offset_ = 0;
  }

  return {
    /** Length of undoing history. */
    get undoLength(): number { return list_.length - offset_ },
    /** Length of redoing history. */
    get redoLength(): number { return offset_; },
    add,
    undo,
    redo,
    clean,
  }
}

export type History<T> = ReturnType<typeof createHist<T>>;
export default createHist;